import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
    ArrowLeft, Film, Link2, Loader2, Pencil, Smartphone, Sparkles, Upload, Download,
    CheckCircle2, XCircle, RotateCcw, LandPlot, Home, BedDouble, Zap, Play, Pause, Music, Eye,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ACCENT = "#0EA5E9";

export default function AdminToolVideoTour() {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const STEP_LABELS = {
        queued: t("tools.video_tour.steps.queued"),
        sunset: t("tools.video_tour.steps.sunset"),
        shots: t("tools.video_tour.steps.shots"),
        assembling: t("tools.video_tour.steps.assembling"),
        done: t("tools.video_tour.steps.done"),
    };
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [url, setUrl] = useState("");
    const [scraping, setScraping] = useState(false);
    const [preparing, setPreparing] = useState(false);
    const [prepProgress, setPrepProgress] = useState({ done: 0, total: 0 });
    const FACT_DEFS = [
        { key: "ground_area", Icon: LandPlot },
        { key: "living_area", Icon: Home },
        { key: "bedrooms", Icon: BedDouble },
        { key: "epc", Icon: Zap },
    ];
    const [facts, setFacts] = useState({
        ground_area: { on: false, value: "" },
        living_area: { on: false, value: "" },
        bedrooms: { on: false, value: "" },
        epc: { on: false, value: "" },
    });
    const [prepared, setPrepared] = useState([]); // all classified photos [{url, category}]
    const [shots, setShots] = useState(null); // ordered selected shots [{url, category}]
    const [storyboard, setStoryboard] = useState([]);
    const dragIndex = useRef(null);
    const [aspect, setAspect] = useState("16:9");
    const [addressLabel, setAddressLabel] = useState("");
    const [logoUrl, setLogoUrl] = useState(null);
    const [locationLabel, setLocationLabel] = useState("");
    const [pinColor, setPinColor] = useState("#E85D26");
    const [pillColor, setPillColor] = useState("#0D0F13");
    const [textColor, setTextColor] = useState("#FFFFFF");
    const [phone, setPhone] = useState("");
    const [website, setWebsite] = useState("");
    const [music, setMusic] = useState("");
    const [tracks, setTracks] = useState([]);
    const [playingKey, setPlayingKey] = useState(null);
    const previewAudioRef = useRef(null);
    const [showOutroPreview, setShowOutroPreview] = useState(false);
    const [outroPreviewUrl, setOutroPreviewUrl] = useState(null);
    const logoRef = useRef(null);
    const brandingLoaded = useRef(false);
    const [submitting, setSubmitting] = useState(false);
    const [job, setJob] = useState(null);
    const [jobs, setJobs] = useState([]);
    const fileRef = useRef(null);
    const pollRef = useRef(null);

    const loadJobs = async () => {
        try {
            const { data } = await api.get("/tools/video-tour/jobs");
            setJobs(data.jobs || []);
        } catch { /* tool access errors handled elsewhere */ }
    };
    useEffect(() => { loadJobs(); }, []);

    useEffect(() => {
        const jid = searchParams.get("job");
        if (jid) {
            api.get(`/tools/video-tour/jobs/${jid}`).then(({ data }) => setJob(data)).catch(() => {});
        }
    }, [searchParams]);

    useEffect(() => {
        api.get("/tools/video-tour/branding").then(({ data }) => {
            if (data.logo_url) setLogoUrl(data.logo_url);
            if (data.pin_color) setPinColor(data.pin_color);
            if (data.pill_color) setPillColor(data.pill_color);
            if (data.text_color) setTextColor(data.text_color);
            if (data.phone) setPhone(data.phone);
            if (data.website) setWebsite(data.website);
            if (data.music) setMusic(data.music);
        }).catch(() => {}).finally(() => { brandingLoaded.current = true; });
    }, []);

    // Auto-save branding per user so they never have to re-pick it
    useEffect(() => {
        if (!brandingLoaded.current) return undefined;
        const tmr = setTimeout(() => {
            api.put("/tools/video-tour/branding", {
                logo_url: logoUrl, pin_color: pinColor, pill_color: pillColor, text_color: textColor,
                phone, website, music: music || null,
            }).catch(() => {});
        }, 800);
        return () => clearTimeout(tmr);
    }, [logoUrl, pinColor, pillColor, textColor, phone, website, music]);

    useEffect(() => {
        api.get("/tools/video-tour/music").then(({ data }) => setTracks(data.tracks || [])).catch(() => {});
        return () => { previewAudioRef.current?.pause(); };
    }, []);

    // Live outro-card preview — regenerates (debounced) while visible
    useEffect(() => {
        if (!showOutroPreview) return undefined;
        setOutroPreviewUrl(null);
        const tmr = setTimeout(() => {
            api.post("/tools/video-tour/outro-preview", {
                logo_url: logoUrl || null, phone, website, pin_color: pinColor, aspect,
                photo_url: prepared[0]?.url || null,
            }, { responseType: "blob" }).then(({ data }) => {
                setOutroPreviewUrl((old) => {
                    if (old) URL.revokeObjectURL(old);
                    return URL.createObjectURL(data);
                });
            }).catch(() => {});
        }, 600);
        return () => clearTimeout(tmr);
    }, [showOutroPreview, logoUrl, phone, website, pinColor, aspect, prepared]);

    const [makingReel, setMakingReel] = useState(false);
    const [editingJobId, setEditingJobId] = useState(null);

    const openEdit = async (jobId) => {
        try {
            const { data } = await api.get(`/tools/video-tour/jobs/${jobId}`);
            if (!storyboard.length) {
                const { data: sb } = await api.get("/tools/video-tour/storyboard");
                setStoryboard(sb.storyboard || []);
            }
            const srcPhotos = (data.source_photos || []).map((p) => (typeof p === "string" ? { url: p, category: "other" } : { ...p }));
            const shotsList = (data.shots || []).map((s) => ({
                url: s.url, category: s.category,
                variant: Number.isInteger(s.variant) ? s.variant : null,
                custom_prompt: s.custom_prompt || "",
            }));
            const prep = [...srcPhotos];
            shotsList.forEach((s) => {
                const ex = prep.find((p) => p.url === s.url);
                if (!ex) prep.push({ url: s.url, category: s.category });
                else if (ex.category === "other") ex.category = s.category;
            });
            setPrepared(prep);
            setShots(shotsList);
            setAddressLabel(data.address_label || "");
            setLocationLabel(data.location_label || "");
            setLogoUrl(data.logo_url || null);
            setPinColor(data.pin_color || "#E85D26");
            setPillColor(data.pill_color || "#0D0F13");
            setTextColor(data.text_color || "#FFFFFF");
            setPhone(data.phone || "");
            setWebsite(data.website || "");
            setMusic(data.music || "");
            setAspect(data.aspect || "16:9");
            const f = { ground_area: { on: false, value: "" }, living_area: { on: false, value: "" }, bedrooms: { on: false, value: "" }, epc: { on: false, value: "" } };
            Object.entries(data.fact_suggestions || {}).forEach(([k, v]) => { if (f[k]) f[k] = { on: false, value: v || "" }; });
            (data.facts || []).forEach((x) => { if (f[x.key]) f[x.key] = { on: true, value: x.value || "" }; });
            setFacts(f);
            setJob(null);
            setEditingJobId(jobId);
            setTimeout(() => document.querySelector('[data-testid="video-tour-storyboard"]')?.scrollIntoView({ behavior: "smooth" }), 150);
        } catch { toast.error(t("tools.video_tour.edit_load_failed")); }
    };

    const cancelEdit = () => {
        setEditingJobId(null);
        setShots(null);
        setPrepared([]);
    };

    const makeReel = async (jobId) => {
        setMakingReel(true);
        try {
            const { data } = await api.post(`/tools/video-tour/jobs/${jobId}/reel`);
            toast.success(t("tools.video_tour.reel_started"));
            const { data: j } = await api.get(`/tools/video-tour/jobs/${data.job_id}`);
            setJob(j);
            loadJobs();
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("tools.video_tour.reel_failed"));
        } finally {
            setMakingReel(false);
        }
    };

    const copyVideoLink = async (jobId) => {
        try {
            const { data } = await api.post(`/tools/video-tour/jobs/${jobId}/share`);
            const url = `${window.location.origin}${data.path}`;
            await navigator.clipboard.writeText(url);
            toast.success(t("tools.video_tour.link_copied"));
        } catch {
            toast.error(t("tools.video_tour.link_copy_failed"));
        }
    };

    const friendlyName = (j) => {
        const loc = (j.address_label || j.location_label || "").trim();
        const slug = loc.replace(/[^A-Za-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "Video";
        const kind = j.aspect === "9:16" ? "Reel" : "Videotour";
        return `AI-${kind}-SocialFactory-${slug}-${(j.id || "").slice(0, 4).toUpperCase()}.mp4`;
    };

    const copyMp4Link = async (resultUrl) => {
        try {
            await navigator.clipboard.writeText(`${BACKEND}${(resultUrl || "").replace("/api/uploads/", "/api/media/")}`);
            toast.success(t("tools.video_tour.mp4_copied"));
        } catch {
            toast.error(t("tools.video_tour.link_copy_failed"));
        }
    };

    const togglePreview = (key) => {
        if (playingKey === key) {
            previewAudioRef.current?.pause();
            setPlayingKey(null);
            return;
        }
        previewAudioRef.current?.pause();
        const a = new Audio(`${BACKEND}/api/public/vtour-music/${key}`);
        a.volume = 0.8;
        a.play().catch(() => {});
        a.onended = () => setPlayingKey(null);
        previewAudioRef.current = a;
        setPlayingKey(key);
    };

    useEffect(() => {
        if (!job || ["completed", "failed"].includes(job.status)) return undefined;
        pollRef.current = setInterval(async () => {
            try {
                const { data } = await api.get(`/tools/video-tour/jobs/${job.id || job.job_id}`);
                setJob(data);
                if (data.status === "completed") {
                    toast.success(t("tools.video_tour.toast_ready"));
                    loadJobs();
                } else if (data.status === "failed") {
                    toast.error(`${t("tools.video_tour.toast_failed", { err: data.error || t("tools.video_tour.unknown_error") })}${data.refunded ? t("tools.video_tour.refunded") : ""}`);
                    loadJobs();
                }
            } catch { /* keep polling */ }
        }, 5000);
        return () => clearInterval(pollRef.current);
    }, [job?.id, job?.job_id, job?.status]);

    const scrape = async () => {
        if (!url.trim()) { toast.error(t("tools.video_tour.toast_link_first")); return; }
        setScraping(true); setShots(null); setPrepared([]);
        try {
            const { data } = await api.post("/tools/video-tour/scrape", { url: url.trim() });
            const imgs = (data.images || []).slice(0, 60);
            toast.success(t("tools.video_tour.toast_found", { count: data.images.length }));
            if (data.facts) {
                setFacts((prev) => {
                    const next = { ...prev };
                    for (const k of Object.keys(next)) {
                        if (data.facts[k]) next[k] = { on: false, value: data.facts[k] };
                    }
                    return next;
                });
            }
            await doPrepare(imgs, false);
        } catch (e) {
            toast.error(e.response?.data?.detail || t("tools.video_tour.toast_scrape_failed"));
        }
        setScraping(false);
    };

    const doPrepare = async (photoUrls, append) => {
        setPreparing(true);
        const CHUNK = 12;
        let first = !append;
        let done = 0;
        const collected = [];
        let sbOrder = storyboard;
        setPrepProgress({ done: 0, total: photoUrls.length });
        try {
            for (let i = 0; i < photoUrls.length; i += CHUNK) {
                const batch = photoUrls.slice(i, i + CHUNK);
                const { data } = await api.post("/tools/video-tour/prepare", { photo_urls: batch });
                sbOrder = data.storyboard || [];
                setStoryboard(sbOrder);
                const photos = data.photos || [];
                collected.push(...photos);
                if (first) {
                    setPrepared(photos);
                    setShots([]); // storyboard wordt pas ingevuld zodra ALLE foto's geladen zijn
                    first = false;
                } else {
                    setPrepared((prev) => [...prev, ...photos.filter((p) => !prev.some((q) => q.url === p.url))]);
                }
                done += batch.length;
                setPrepProgress({ done, total: photoUrls.length });
            }
            toast.success(t("tools.video_tour.toast_rooms"));
        } catch (e) {
            toast.error(e.response?.data?.detail || t("tools.video_tour.toast_prepare_failed"));
        }
        // Suggestie pas berekenen over ALLE binnengekomen foto's — eerste foto
        // per categorie, in de cinematische storyboard-volgorde (voorgevel eerst).
        if (!append && collected.length) {
            const used = new Set();
            const sug = [];
            for (const cat of (sbOrder || []).map((c) => c.key)) {
                const p = collected.find((x) => x.category === cat && !used.has(x.url));
                if (p) { sug.push({ url: p.url, category: cat, variant: null }); used.add(p.url); }
            }
            setShots(sug.length ? sug : collected.slice(0, 9).map((p) => ({ url: p.url, category: p.category !== "other" ? p.category : "living", variant: null })));
        }
        setPreparing(false);
    };

    const uploadFiles = async (files) => {
        const urls = [];
        for (const f of Array.from(files).slice(0, 20)) {
            if (!f.type.startsWith("image/")) continue;
            const fd = new FormData();
            fd.append("file", f);
            try {
                const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                urls.push(data.url);
            } catch { toast.error(t("tools.video_tour.toast_upload_failed", { name: f.name })); }
        }
        if (urls.length) {
            if (!shots) setShots([]);
            await doPrepare(urls, prepared.length > 0);
        }
    };

    const uploadLogo = async (file) => {
        if (!file || !file.type.startsWith("image/")) return;
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setLogoUrl(data.url);
            toast.success(t("tools.video_tour.toast_logo_ok"));
        } catch { toast.error(t("tools.video_tour.toast_logo_fail")); }
    };

    const generate = async () => {
        const valid = (shots || []).filter((s) => s.category !== "other");
        if (valid.length < 3) { toast.error(t("tools.video_tour.toast_min_shots")); return; }
        const picks = pickVariants(valid);
        const payload = valid.map((s, i) => ({ ...s, variant: picks[i] }));
        setSubmitting(true);
        try {
            const endpoint = editingJobId ? `/tools/video-tour/jobs/${editingJobId}/update` : "/tools/video-tour/jobs";
            const { data } = await api.post(endpoint, {
                shots: payload, aspect, address_label: addressLabel,
                logo_url: logoUrl, location_label: locationLabel, pin_color: pinColor,
                pill_color: pillColor, text_color: textColor,
                phone, website, music: music || null,
                all_photos: (prepared || []).map((p) => ({ url: p.url, category: p.category || "other" })),
                fact_values: Object.fromEntries(Object.entries(facts).map(([k, f]) => [k, (f.value || "").trim()])),
                facts: Object.entries(facts)
                    .filter(([, f]) => f.on && f.value.trim())
                    .map(([key, f]) => ({ key, value: f.value.trim() })),
            });
            setJob({ id: data.job_id, status: "processing", step: "queued", shots_done: 0, shots_total: valid.length });
            if (editingJobId) {
                setEditingJobId(null);
                toast.success(t("tools.video_tour.update_started", { count: data.regenerated ?? 0 }));
            } else {
                toast.success(t("tools.video_tour.toast_started"));
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || t("tools.video_tour.toast_start_failed"));
        }
        setSubmitting(false);
    };

    const catLabel = (key) => t(`tools.video_tour.cats.${key}`, (storyboard.find((s) => s.key === key) || {}).label || key);
    const variantsFor = (key) => (storyboard.find((c) => c.key === key) || {}).variants || [];
    // Mirror of backend _pick_variants. A manual choice (s.variant) always wins;
    // auto shots avoid repeating the previous shot's motion type.
    const pickVariants = (list) => {
        const picks = [];
        let prev = null;
        (list || []).forEach((s, i) => {
            const variants = variantsFor(s.category);
            if (!variants.length) { picks.push(0); return; }
            let idx;
            if (Number.isInteger(s.variant) && s.variant >= 0 && s.variant < variants.length) {
                idx = s.variant;
            } else {
                const start = s.category === "garden_sunset" ? 0 : i;
                idx = start % variants.length;
                for (let k = 0; k < variants.length; k += 1) {
                    const j = (start + k) % variants.length;
                    if (variants[j].type !== prev) { idx = j; break; }
                }
            }
            picks.push(idx);
            prev = variants[idx].type;
        });
        return picks;
    };
    const variantPicks = pickVariants(shots);
    const motionText = (cat, v) => t(`tools.video_tour.motion_${v?.type || "arc"}`, { room: catLabel(cat) });
    const motionFor = (key, i) => motionText(key, variantsFor(key)[variantPicks[i]]);
    const toggleShot = (p) => {
        setShots((prev) => {
            const list = prev || [];
            const idx = list.findIndex((s) => s.url === p.url);
            if (idx >= 0) return list.filter((_, j) => j !== idx);
            if (list.length >= 10) { toast.error(t("tools.video_tour.toast_max_shots")); return list; }
            return [...list, { url: p.url, category: p.category !== "other" ? p.category : "living", variant: null }];
        });
    };
    const onDrop = (i) => {
        const from = dragIndex.current;
        if (from === null || from === i) return;
        setShots((prev) => {
            const next = [...prev];
            const [moved] = next.splice(from, 1);
            next.splice(i, 0, moved);
            return next;
        });
        dragIndex.current = null;
    };

    return (
        <div className="min-h-screen bg-[#FAFAFA] text-zinc-950" data-testid="tool-video-tour-page">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-8">
                <div className="flex items-center justify-between mb-8">
                    <Link to="/tools" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-950 transition" data-testid="video-tour-back">
                        <ArrowLeft className="h-4 w-4" /> Tools
                    </Link>
                    <div className="text-xs text-zinc-500">
                        {isAdmin ? <span style={{ color: ACCENT }}>{t("tools.video_tour.admin_free")}</span>
                            : !user?.free_video_tour_used ? <span className="font-bold" style={{ color: ACCENT }} data-testid="video-tour-first-free">{t("tools.video_tour.first_free")}</span>
                            : <span>{t("tools.video_tour.cost")} <span className="text-zinc-950 font-bold">15 credits</span> {t("tools.video_tour.per_video")}</span>}
                    </div>
                </div>

                <p className="text-xs uppercase tracking-[0.18em] mb-3 flex items-center gap-2" style={{ color: ACCENT }}>
                    <Film className="h-3.5 w-3.5" /> AI Tool
                </p>
                <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">{t("tools.video_tour.title")}</h1>
                <p className="mt-3 text-sm text-zinc-500 max-w-2xl mb-10">{t("tools.video_tour.subtitle")}</p>

                {/* STEP 1 — link / upload */}
                <div className="bg-white border border-zinc-200 rounded-md p-5 mb-6" data-testid="video-tour-step1">
                    <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-3">{t("tools.video_tour.step1")}</p>
                    <div className="flex flex-col sm:flex-row gap-2">
                        <div className="relative flex-1">
                            <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                            <input
                                value={url}
                                onChange={(e) => setUrl(e.target.value)}
                                placeholder="https://www.immoweb.be/nl/zoekertje/…"
                                className="w-full h-11 pl-9 pr-3 bg-white border border-zinc-200 rounded-sm text-sm outline-none focus:border-[#0EA5E9]"
                                data-testid="video-tour-url-input"
                            />
                        </div>
                        <Button onClick={scrape} disabled={scraping} className="h-11 px-5 text-white" style={{ background: ACCENT }} data-testid="video-tour-scrape-btn">
                            {scraping ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
                            {t("tools.video_tour.fetch")}
                        </Button>
                        <Button variant="outline" onClick={() => fileRef.current?.click()} className="h-11 px-4 border-zinc-200" data-testid="video-tour-upload-btn">
                            <Upload className="h-4 w-4 mr-2" /> {t("tools.video_tour.or_upload")}
                        </Button>
                        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden"
                            onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} />
                    </div>

                    {(scraping || preparing) && (
                        <p className="mt-4 text-xs text-zinc-500 flex items-center gap-2" data-testid="video-tour-preparing">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" style={{ color: ACCENT }} />
                            {scraping && !preparing ? t("tools.video_tour.fetching") : `${t("tools.video_tour.classifying")}${prepProgress.total > 12 ? ` (${prepProgress.done}/${prepProgress.total})` : ""}`}
                        </p>
                    )}
                </div>

                {/* STEP 2 — pick photos + drag storyboard */}
                {shots && (
                    <div className="bg-white border border-zinc-200 rounded-md p-5 mb-6" data-testid="video-tour-storyboard">
                        {editingJobId && (
                            <div className="mb-4 flex items-center justify-between gap-3 rounded-sm border border-[#0EA5E9]/40 bg-[#0EA5E9]/5 px-3 py-2" data-testid="video-tour-editing-banner">
                                <p className="text-xs text-[#0369A1]">{t("tools.video_tour.editing_banner")}</p>
                                <button type="button" onClick={cancelEdit} className="text-[11px] text-zinc-500 hover:text-zinc-900 shrink-0" data-testid="video-tour-cancel-edit">
                                    {t("tools.video_tour.cancel_edit")}
                                </button>
                            </div>
                        )}
                        <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">{t("tools.video_tour.step2")}</p>
                        <p className="text-xs text-zinc-400 mb-3">{t("tools.video_tour.step2_hint")}</p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
                            {prepared.map((p) => {
                                const pos = (shots || []).findIndex((s) => s.url === p.url);
                                const on = pos >= 0;
                                return (
                                    <button key={p.url} type="button" onClick={() => toggleShot(p)}
                                        className={`relative aspect-[4/3] rounded-sm overflow-hidden border-2 transition ${on ? "border-[#0EA5E9]" : "border-transparent opacity-45 hover:opacity-90"}`}
                                        data-testid="video-tour-pick-photo"
                                    >
                                        <img src={`${BACKEND}${p.url}`} alt="" className="w-full h-full object-cover" loading="lazy" />
                                        <span className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[11px] px-1.5 py-1 truncate">{catLabel(p.category === "other" ? "living" : p.category)}</span>
                                        {on && <span className="absolute top-1.5 right-1.5 h-6 w-6 rounded-full bg-[#0EA5E9] text-white text-xs font-bold flex items-center justify-center">{pos + 1}</span>}
                                    </button>
                                );
                            })}
                        </div>

                        <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">{t("tools.video_tour.step3")}</p>
                        <p className="text-xs text-zinc-400 mb-3">{t("tools.video_tour.step3_hint")}</p>
                        {preparing && shots.length === 0 && (
                            <p className="text-xs text-[#E5A93D] mb-3 flex items-center gap-2" data-testid="video-tour-ordering-pending">
                                <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t("tools.video_tour.ordering_pending")}
                            </p>
                        )}
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                            {shots.map((s, i) => (
                                <div key={s.url}
                                    draggable
                                    onDragStart={() => { dragIndex.current = i; }}
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={() => onDrop(i)}
                                    className="border border-zinc-200 rounded-sm overflow-hidden cursor-grab active:cursor-grabbing bg-white hover:border-[#0EA5E9]/60 transition"
                                    data-testid={`video-tour-shot-${i}`}
                                >
                                    <div className="relative">
                                        <img src={`${BACKEND}${s.url}`} alt="" className="w-full aspect-[4/3] object-cover pointer-events-none" />
                                        <span className="absolute top-1.5 left-1.5 h-6 w-6 rounded-full bg-[#0EA5E9] text-white text-[11px] font-bold flex items-center justify-center shadow">{i + 1}</span>
                                        <span className="absolute top-1.5 right-1.5 text-white/90 text-sm select-none" title={t("tools.video_tour.drag_hint")}>⠿</span>
                                    </div>
                                    <div className="p-2 space-y-1.5">
                                        <select
                                            value={s.category}
                                            onChange={(e) => setShots((prev) => prev.map((p, j) => j === i ? { ...p, category: e.target.value, variant: null } : p))}
                                            className="w-full text-[11px] bg-white border border-zinc-200 rounded-sm h-7 px-1"
                                            data-testid={`video-tour-shot-cat-${i}`}
                                        >
                                            {storyboard.map((c) => <option key={c.key} value={c.key}>{catLabel(c.key)}</option>)}
                                        </select>
                                        <select
                                            value={Number.isInteger(s.variant) ? String(s.variant) : "auto"}
                                            onChange={(e) => {
                                                const v = e.target.value === "auto" ? null : Number(e.target.value);
                                                setShots((prev) => prev.map((p, j) => j === i ? { ...p, variant: v } : p));
                                            }}
                                            className="w-full text-[11px] bg-[#0EA5E9]/10 border border-[#0EA5E9]/30 text-[#0369A1] rounded-sm h-7 px-1"
                                            data-testid={`video-tour-shot-effect-${i}`}
                                            title={t("tools.video_tour.effect_label")}
                                        >
                                            <option value="auto">🎥 {t("tools.video_tour.auto")}: {motionFor(s.category, i)}</option>
                                            {variantsFor(s.category).map((v, k) => (
                                                <option key={k} value={k}>🎥 {v.motion}</option>
                                            ))}
                                        </select>
                                        <input
                                            value={s.custom_prompt || ""}
                                            onChange={(e) => setShots((prev) => prev.map((p, j) => j === i ? { ...p, custom_prompt: e.target.value } : p))}
                                            placeholder={t("tools.video_tour.shot_prompt_ph")}
                                            maxLength={200}
                                            className="w-full text-[10px] bg-white border border-zinc-200 rounded-sm h-7 px-1.5 outline-none focus:border-zinc-400"
                                            data-testid={`video-tour-shot-prompt-${i}`}
                                        />
                                        <button type="button" onClick={() => setShots((prev) => prev.filter((_, j) => j !== i))}
                                            className="text-[10px] text-zinc-400 hover:text-red-600">{t("tools.video_tour.remove")}</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <p className="text-[11px] text-zinc-400 mt-3">{t("tools.video_tour.shots_count", { count: shots.length, sec: Math.max(Math.round(shots.length * 2.5), 3) })}</p>

                        <div className="mt-5 border border-zinc-200 rounded-sm p-3 bg-zinc-50/60" data-testid="video-tour-facts">
                            <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">{t("tools.video_tour.facts_title")}</p>
                            <p className="text-xs text-zinc-400 mb-3">{t("tools.video_tour.facts_hint")}</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                                {FACT_DEFS.map(({ key, Icon }) => (
                                    <label key={key} className={`flex items-center gap-2 p-2 rounded-sm border cursor-pointer transition ${facts[key].on ? "border-[#0EA5E9] bg-white" : "border-zinc-200 bg-white/60"}`} data-testid={`video-tour-fact-${key}`}>
                                        <input
                                            type="checkbox"
                                            checked={facts[key].on}
                                            onChange={(e) => setFacts((p) => ({ ...p, [key]: { ...p[key], on: e.target.checked } }))}
                                            data-testid={`video-tour-fact-toggle-${key}`}
                                            className="h-4 w-4 rounded-sm shrink-0"
                                            style={{ accentColor: pinColor }}
                                        />
                                        <Icon className="h-4 w-4 shrink-0" style={{ color: pinColor }} />
                                        <div className="min-w-0 flex-1">
                                            <p className="text-[10px] uppercase tracking-wider text-zinc-500 truncate">{t(`tools.video_tour.fact_${key}`)}</p>
                                            <input
                                                value={facts[key].value}
                                                onChange={(e) => setFacts((p) => ({ ...p, [key]: { ...p[key], value: e.target.value } }))}
                                                onClick={(e) => e.preventDefault()}
                                                placeholder={key === "bedrooms" ? "4" : key === "epc" ? "B / 250 kWh/m²" : "250 m²"}
                                                maxLength={20}
                                                data-testid={`video-tour-fact-value-${key}`}
                                                className="w-full bg-transparent text-sm text-zinc-900 outline-none border-b border-transparent focus:border-zinc-300"
                                            />
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </div>

                        <div className="mt-5 border border-zinc-200 rounded-sm p-3 bg-zinc-50/60" data-testid="video-tour-branding">
                            <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">{t("tools.video_tour.branding")}</p>
                            <div className="flex flex-wrap items-center gap-3">
                                <div className="flex items-center gap-2">
                                    {logoUrl ? (
                                        <>
                                            <img src={`${BACKEND}${logoUrl}`} alt="logo" className="h-10 w-10 object-contain bg-white border border-zinc-200 rounded-sm p-0.5" />
                                            <button type="button" onClick={() => setLogoUrl(null)} className="text-[10px] text-zinc-400 hover:text-red-600" data-testid="video-tour-logo-remove">{t("tools.video_tour.remove")}</button>
                                        </>
                                    ) : (
                                        <Button variant="outline" onClick={() => logoRef.current?.click()} className="h-9 px-3 border-zinc-200 text-xs" data-testid="video-tour-logo-btn">
                                            <Upload className="h-3.5 w-3.5 mr-1.5" /> {t("tools.video_tour.logo_btn")}
                                        </Button>
                                    )}
                                    <input ref={logoRef} type="file" accept="image/*" className="hidden"
                                        onChange={(e) => { uploadLogo(e.target.files?.[0]); e.target.value = ""; }} />
                                </div>
                                <input
                                    value={locationLabel}
                                    onChange={(e) => setLocationLabel(e.target.value)}
                                    placeholder={t("tools.video_tour.location_ph")}
                                    className="h-9 px-3 bg-white border border-zinc-200 rounded-sm text-xs outline-none w-52"
                                    data-testid="video-tour-location-input"
                                />
                                <label className="flex items-center gap-2 text-xs text-zinc-500">
                                    {t("tools.video_tour.pin_color")}
                                    <input type="color" value={pinColor} onChange={(e) => setPinColor(e.target.value)}
                                        className="h-8 w-10 border border-zinc-200 rounded-sm cursor-pointer bg-white"
                                        data-testid="video-tour-pin-color" />
                                </label>
                                <label className="flex items-center gap-2 text-xs text-zinc-500">
                                    {t("tools.video_tour.pill_color")}
                                    <input type="color" value={pillColor} onChange={(e) => setPillColor(e.target.value)}
                                        className="h-8 w-10 border border-zinc-200 rounded-sm cursor-pointer bg-white"
                                        data-testid="video-tour-pill-color" />
                                </label>
                                <label className="flex items-center gap-2 text-xs text-zinc-500">
                                    {t("tools.video_tour.text_color")}
                                    <input type="color" value={textColor} onChange={(e) => setTextColor(e.target.value)}
                                        className="h-8 w-10 border border-zinc-200 rounded-sm cursor-pointer bg-white"
                                        data-testid="video-tour-text-color" />
                                </label>
                                {locationLabel && (
                                    <span className="inline-flex items-center gap-1.5 text-xs font-bold rounded-full px-2.5 h-8"
                                        style={{ background: `${pillColor}D0`, color: textColor }}
                                        data-testid="video-tour-location-preview">
                                        <svg width="12" height="15" viewBox="0 0 12 15"><path d="M6 0C2.7 0 0 2.6 0 5.8 0 10 6 15 6 15s6-5 6-9.2C12 2.6 9.3 0 6 0z" fill={pinColor} /><circle cx="6" cy="5.6" r="2" fill={pillColor} /></svg>
                                        {locationLabel}
                                    </span>
                                )}
                            </div>
                            <div className="flex flex-wrap items-center gap-3 mt-3">
                                <span className="text-[11px] uppercase tracking-wider text-zinc-500">{t("tools.video_tour.outro")}</span>
                                <input
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                    placeholder={t("tools.video_tour.phone_ph")}
                                    className="h-9 px-3 bg-white border border-zinc-200 rounded-sm text-xs outline-none w-44"
                                    data-testid="video-tour-phone-input"
                                />
                                <input
                                    value={website}
                                    onChange={(e) => setWebsite(e.target.value)}
                                    placeholder={t("tools.video_tour.website_ph")}
                                    className="h-9 px-3 bg-white border border-zinc-200 rounded-sm text-xs outline-none w-52"
                                    data-testid="video-tour-website-input"
                                />
                                <span className="text-[10px] text-zinc-400">{t("tools.video_tour.outro_hint")}</span>
                                <button type="button" onClick={() => setShowOutroPreview((v) => !v)}
                                    className="h-9 px-3 text-xs rounded-sm border border-zinc-200 bg-white text-zinc-700 hover:border-zinc-400 inline-flex items-center gap-1.5"
                                    data-testid="video-tour-outro-preview-btn">
                                    <Eye className="h-3.5 w-3.5" /> {t("tools.video_tour.outro_preview")}
                                </button>
                            </div>
                            {showOutroPreview && (
                                <div className="mt-3" data-testid="video-tour-outro-preview">
                                    {outroPreviewUrl ? (
                                        <img src={outroPreviewUrl} alt="Outro preview"
                                            className={`rounded-sm border border-zinc-200 ${aspect === "16:9" ? "max-w-lg w-full" : "max-h-96"}`} />
                                    ) : (
                                        <div className="flex items-center gap-2 text-xs text-zinc-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Preview laden...</div>
                                    )}
                                </div>
                            )}
                            <div className="flex flex-wrap items-center gap-2 mt-3">
                                <span className="text-[11px] uppercase tracking-wider text-zinc-500 flex items-center gap-1"><Music className="h-3 w-3" /> {t("tools.video_tour.music")}</span>
                                <button type="button" onClick={() => setMusic("")}
                                    className={`h-8 px-2.5 text-xs rounded-sm border ${music === "" ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-200 bg-white text-zinc-600"}`}
                                    data-testid="video-tour-music-none">{t("tools.video_tour.music_none")}</button>
                                {tracks.map((tr) => (
                                    <div key={tr.key} className={`flex items-center rounded-sm border overflow-hidden ${music === tr.key ? "border-zinc-900" : "border-zinc-200"}`}>
                                        <button type="button" onClick={() => setMusic(tr.key)}
                                            className={`h-8 px-2.5 text-xs ${music === tr.key ? "bg-zinc-900 text-white" : "bg-white text-zinc-600"}`}
                                            data-testid={`video-tour-music-${tr.key}`}>{tr.label}</button>
                                        <button type="button" onClick={() => togglePreview(tr.key)}
                                            className="h-8 px-2 bg-white text-zinc-500 border-l border-zinc-200 hover:text-zinc-900"
                                            data-testid={`video-tour-music-preview-${tr.key}`}>
                                            {playingKey === tr.key ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="mt-5 flex flex-wrap items-center gap-3">
                            <div className="flex items-center gap-1 bg-zinc-100 rounded-sm p-1">
                                {["16:9", "9:16"].map((a) => (
                                    <button key={a} type="button" onClick={() => setAspect(a)} disabled={!!editingJobId}
                                        className={`px-3 h-8 text-xs rounded-sm transition disabled:opacity-50 ${aspect === a ? "bg-white shadow-sm font-bold" : "text-zinc-500"}`}
                                        data-testid={`video-tour-aspect-${a}`}>
                                        {a === "16:9" ? "16:9 · Web" : "9:16 · Reels"}
                                    </button>
                                ))}
                            </div>
                            <input
                                value={addressLabel}
                                onChange={(e) => setAddressLabel(e.target.value)}
                                placeholder={t("tools.video_tour.name_ph")}
                                className="h-9 px-3 bg-white border border-zinc-200 rounded-sm text-xs outline-none flex-1 min-w-[200px]"
                                data-testid="video-tour-address-input"
                            />
                            <Button onClick={generate} disabled={submitting} className="h-11 px-6 text-white font-bold" style={{ background: ACCENT }} data-testid="video-tour-generate-btn">
                                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Film className="h-4 w-4 mr-2" />}
                                {editingJobId
                                    ? `${t("tools.video_tour.update_video")} (${isAdmin ? t("tools.video_tour.free") : t("tools.video_tour.update_cost")})`
                                    : `${t("tools.video_tour.generate")} (${isAdmin ? t("tools.video_tour.free") : "15 credits"})`}
                            </Button>
                        </div>
                    </div>
                )}

                {/* Active job progress */}
                {job && (
                    <div className="bg-white border rounded-md p-5 mb-6" style={{ borderColor: `${ACCENT}55` }} data-testid="video-tour-job-status">
                        {job.status === "completed" ? (
                            <div>
                                <p className="text-sm font-bold mb-3 flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> {t("tools.video_tour.ready")}</p>
                                <video src={`${BACKEND}${job.result_url}`} controls className={`rounded-sm bg-black ${job.aspect === "9:16" ? "max-h-[480px]" : "w-full max-w-2xl"}`} />
                                <div className="flex flex-wrap items-center gap-2 mt-3">
                                    <a href={`${BACKEND}${job.result_url}?dl=${encodeURIComponent(friendlyName(job))}`}
                                        className="inline-flex items-center gap-2 text-xs px-4 h-9 text-white rounded-sm" style={{ background: ACCENT }}
                                        data-testid="video-tour-download-btn">
                                        <Download className="h-3.5 w-3.5" /> {t("tools.video_tour.download")}
                                    </a>
                                    <button type="button" onClick={() => copyVideoLink(job.id)}
                                        className="inline-flex items-center gap-2 text-xs px-4 h-9 rounded-sm border border-zinc-300 bg-white text-zinc-800 hover:border-zinc-500"
                                        data-testid="video-tour-copy-link-btn">
                                        <Link2 className="h-3.5 w-3.5" /> {t("tools.video_tour.copy_link")}
                                    </button>
                                    <button type="button" onClick={() => copyMp4Link(job.result_url)}
                                        className="inline-flex items-center gap-2 text-xs px-4 h-9 rounded-sm border border-zinc-300 bg-white text-zinc-800 hover:border-zinc-500"
                                        data-testid="video-tour-copy-mp4-btn">
                                        <Film className="h-3.5 w-3.5" /> {t("tools.video_tour.copy_mp4")}
                                    </button>
                                    {job.aspect === "16:9" && (
                                        <button type="button" onClick={() => makeReel(job.id)} disabled={makingReel}
                                            className="inline-flex items-center gap-2 text-xs px-4 h-9 rounded-sm border border-zinc-300 bg-white text-zinc-800 hover:border-zinc-500 disabled:opacity-50"
                                            data-testid="video-tour-make-reel-btn">
                                            {makingReel ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Smartphone className="h-3.5 w-3.5" />} {t("tools.video_tour.make_reel")}
                                        </button>
                                    )}
                                </div>
                            </div>
                        ) : job.status === "failed" ? (
                            <p className="text-sm text-red-600 flex items-center gap-2"><XCircle className="h-4 w-4" /> {t("tools.video_tour.failed")}: {job.error}{job.refunded ? t("tools.video_tour.refunded") : ""}</p>
                        ) : (
                            <div>
                                <p className="text-sm font-bold flex items-center gap-2">
                                    <Loader2 className="h-4 w-4 animate-spin" style={{ color: ACCENT }} />
                                    {STEP_LABELS[job.step] || t("tools.video_tour.busy")}
                                    {job.step === "shots" && <span className="text-zinc-500 font-normal">{t("tools.video_tour.shot")} {Math.min((job.shots_done ?? 0) + 1, job.shots_total)} / {job.shots_total}</span>}
                                    {job.step === "assembling" && job.assemble_total > 0 && <span className="text-zinc-500 font-normal">{Math.min((job.assemble_done ?? 0) + 1, job.assemble_total)} / {job.assemble_total}</span>}
                                </p>
                                <div className="mt-3 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                                    <div className="h-full rounded-full transition-all" style={{ background: ACCENT, width: `${Math.round(((job.shots_done || 0) / Math.max(job.shots_total || 1, 1)) * 85) + (job.step === "assembling" ? 90 : 5)}%`, maxWidth: "97%" }} />
                                </div>
                                <p className="text-[11px] text-zinc-500 mt-2">{t("tools.video_tour.takes")}</p>
                            </div>
                        )}
                    </div>
                )}

                {/* Recent videos */}
                {jobs.length > 0 && (
                    <div className="mt-8" data-testid="video-tour-history">
                        <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-3">{t("tools.video_tour.recent")}</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {jobs.map((j) => (
                                <div key={j.id} className="bg-white border border-zinc-200 rounded-sm p-3 text-xs" data-testid={`video-tour-history-${j.id}`}>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="font-bold">{j.address_label || t("tools.video_tour.default_name")} · {j.aspect}</span>
                                        <span className={j.status === "completed" ? "text-emerald-600" : j.status === "failed" ? "text-red-600" : "text-zinc-500"}>
                                            {j.status === "completed" ? t("tools.video_tour.steps.done") : j.status === "failed" ? t("tools.video_tour.failed") : STEP_LABELS[j.step] || t("tools.video_tour.busy")}
                                        </span>
                                    </div>
                                    {j.status === "failed" && j.error && (
                                        <p className="text-[10px] text-red-600 bg-red-50 border border-red-100 rounded-sm px-2 py-1 mb-1.5 break-words" data-testid={`video-tour-error-${j.id}`}>
                                            {j.error}{j.refunded ? t("tools.video_tour.refunded") : ""}
                                        </p>
                                    )}
                                    {j.status === "completed" && j.result_url && (
                                        <div>
                                            {j.error && (
                                                <p className="text-[10px] text-amber-600 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1 mb-1.5" data-testid={`video-tour-restored-${j.id}`}>
                                                    ⚠ {t("tools.video_tour.edit_failed_restored")}
                                                </p>
                                            )}
                                            <video src={`${BACKEND}${j.result_url}`} controls preload="metadata" className="w-full rounded-sm bg-black" />
                                            <div className="flex items-center gap-2 mt-2">
                                                <a href={`${BACKEND}${j.result_url}?dl=${encodeURIComponent(friendlyName(j))}`} className="inline-flex items-center gap-1 text-[11px]" style={{ color: ACCENT }} data-testid={`video-tour-history-download-${j.id}`}>
                                                    <Download className="h-3 w-3" /> {t("tools.video_tour.download")}
                                                </a>
                                                <button type="button" onClick={() => copyVideoLink(j.id)} className="inline-flex items-center gap-1 text-[11px] text-zinc-600 hover:text-zinc-900" data-testid={`video-tour-history-copy-${j.id}`}>
                                                    <Link2 className="h-3 w-3" /> {t("tools.video_tour.copy_link")}
                                                </button>
                                                <button type="button" onClick={() => copyMp4Link(j.result_url)} className="inline-flex items-center gap-1 text-[11px] text-zinc-600 hover:text-zinc-900" data-testid={`video-tour-history-mp4-${j.id}`}>
                                                    <Film className="h-3 w-3" /> {t("tools.video_tour.copy_mp4")}
                                                </button>
                                                {j.aspect === "16:9" && (
                                                    <button type="button" onClick={() => makeReel(j.id)} disabled={makingReel} className="inline-flex items-center gap-1 text-[11px] text-zinc-600 hover:text-zinc-900 disabled:opacity-50" data-testid={`video-tour-history-reel-${j.id}`}>
                                                        <Smartphone className="h-3 w-3" /> {t("tools.video_tour.make_reel")}
                                                    </button>
                                                )}
                                                <button type="button" onClick={() => openEdit(j.id)} className="inline-flex items-center gap-1 text-[11px] text-zinc-600 hover:text-zinc-900" data-testid={`video-tour-history-edit-${j.id}`}>
                                                    <Pencil className="h-3 w-3" /> {t("tools.video_tour.edit")}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                    {["processing"].includes(j.status) && !job && (
                                        <button type="button" onClick={() => setJob(j)} className="inline-flex items-center gap-1 text-[11px]" style={{ color: ACCENT }}>
                                            <RotateCcw className="h-3 w-3" /> {t("tools.video_tour.follow")}
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
